import "./LoadProfile.css";
import React, { useEffect, useState, useRef } from "react";
import { PulseLoader } from "react-spinners";
import { Upload, ExclamationTriangle } from "react-bootstrap-icons";
import Modal from "react-modal";
import axiosInstance from "../../../utils/axiosConfig";
import { loadProfiles, years } from "../../../mock/MockData";
import { toast, ToastContainer } from "react-toastify";

const Settings: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>(
    new Date().getFullYear().toString()
  );
  const [selectedLoadProfile, setSelectedLoadProfile] = useState("LP1");
  const [loadProfile, setLoadProfile] = useState<any | undefined>();
  const [loadProfileColumns, setLoadProfileColumns] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fileData, setFileData] = useState<any>();
  const [showConfirmUpload, setShowConfirmUpload] = useState<boolean>(false);

  const closeConfirmUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setShowConfirmUpload(false);
  };

  useEffect(() => {
    if (
      selectedYear &&
      selectedYear !== "" &&
      selectedLoadProfile &&
      selectedLoadProfile != ""
    ) {
      getLoadProfile();
    }
  }, [selectedYear, selectedLoadProfile]);

  const getLoadProfile = async () => {
    setIsLoading(true);

    axiosInstance.get(`${process.env.REACT_APP_API_URL}loadprofile/${selectedYear}/${selectedLoadProfile}`)
      .then(response => {
        const data = response.data;
        setLoadProfile(data);
        var autumnDst = data.Days.filter((x: any) => x.DayNotes == "Autumn DST");
        var keys: string[] = [];

        if (autumnDst.length > 0) {
          keys = Object.keys(autumnDst[0]);
          var dstIndex = keys.findIndex(k => k.includes("dst"));
          var dstColumns = keys.splice(dstIndex, 4);
          keys.push(...dstColumns);
        } else {
          keys = Object.keys(data.Days[0]);
        }

        setLoadProfileColumns(keys);
      })
      .catch(error => {
        setLoadProfile(null);
        setLoadProfileColumns([]);

        console.error("Error fetching data from table:", error);
        setIsLoading(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    
    const selectedFile = event.target.files[0];
    
    if (selectedFile.type !== "text/csv") {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      
      toast.error("Invalid file type. Please upload a CSV file.");
      return;
    }

    const formData = new FormData();
    formData.append("File", event.target.files![0]);

    setFileData(event.target.files![0]);
    setShowConfirmUpload(true);
  };

  const uploadLoadProfile = async () => {
    setIsLoading(true);

    const formData = new FormData();
    formData.append("file", fileData);

    axiosInstance.post(`${process.env.REACT_APP_API_URL}loadprofile/${selectedYear}/${selectedLoadProfile}`, formData)
      .then(response => {
        getLoadProfile();
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      })

    closeConfirmUpload();
  };

  return (
    <div>
      {showConfirmUpload && (
        <Modal
          isOpen={showConfirmUpload}
          style={{
            content: {
              top: "50%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              marginRight: "-50%",
              transform: "translate(-50%, -50%)",
              padding: "30px",
            },
          }}
        >
          <label className="modal-title">Confirm upload</label>
          <label className="modal-content">
            Are you sure you want to upload {fileData.name} to {selectedYear} {selectedLoadProfile}?
          </label>
          {loadProfile && (
            <div className="modal-warning-container">
              <ExclamationTriangle size={25} />
              <label>This will replace the current load profile values</label>
            </div>
          )}
          <div className="modal-buttons-container">
            <div
              className="modal-button-secondary"
              onClick={closeConfirmUpload}
            >
              Cancel
            </div>
            <div className="modal-button-primary" onClick={uploadLoadProfile}>
              Upload
            </div>
          </div>
        </Modal>
      )}
      <div className="page-title">
        <p>Load Profile</p>
      </div>
      <div className="loadprofile-table-select-container">
        <select
          className="loadprofile-select-table"
          onChange={(event) => setSelectedYear(event.target.value)}
          value={selectedYear}
        >
          {years.map((year) => (
            <option value={year} key={year}>
              {year}
            </option>
          ))}
        </select>
        <select
          className="loadprofile-select-table"
          onChange={(event) => setSelectedLoadProfile(event.target.value)}
        >
          {loadProfiles.map((loadProfile) => (
            <option value={loadProfile} key={loadProfile}>
              {loadProfile}
            </option>
          ))}
        </select>
      </div>
      <div className="loadprofile-upload-container">
        <div className="card">
          <div className="card-body">
            <label className="loadprofile-upload m-2">
              Upload the latest load profile
            </label>
            <div className="loadprofile-input-container">
              <label
                className="loadprofile-upload"
                htmlFor="loadprofile-upload-input"
              >
                <Upload />
                Upload CSV
              </label>
              <input
                className="loadprofile-upload-input"
                id="loadprofile-upload-input"
                type="file"
                name="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={onSelectFile}
                disabled={selectedYear == "" || selectedLoadProfile == ""}
              />
            </div>
          </div>
        </div>
      </div>
      {!isLoading && loadProfile && (
        <div className="load-profile-details">
          <label className="load-profile-description">
            {loadProfile.Description}
          </label>
          <div>
            Indices:
            <label className="load-profile-indices">
              {loadProfile.Indices}
            </label>
          </div>
        </div>
      )}
      {!isLoading && loadProfileColumns && loadProfileColumns.length > 0 ? (
        <div className="loadprofile-table-container">
          <table>
            <thead>
              <tr>
                {loadProfileColumns.map((column) => {
                  if (
                    column !== "etag" &&
                    column != "partitionKey" &&
                    column != "timestamp"
                  ) {
                    const columnName = column == "rowKey" ? "Date" : column;
                    return <th key={columnName}>{columnName}</th>;
                  }
                })}
              </tr>
            </thead>
            <tbody>
              {loadProfile.Days.map((entity: any, index: number) => (
                <tr key={index}>
                  {loadProfileColumns.map((column) => {
                    if (
                      column !== "etag" &&
                      column != "partitionKey" &&
                      column != "timestamp"
                    )
                      return <td key={column}>{entity[column]}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="loadprofile-pretable-container">
          {isLoading ? (
            <PulseLoader />
          ) : (
            <div className="loadprofile-no-data">No data has been added for this load profile</div>
          )}
        </div>
      )}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default Settings;
